// import { useNavigate } from "react-router-dom";  // form-only: goHome uses window.location now
import { Button } from "@/components/ui/button";
import { Check, Gift, Mail, Calendar, Phone, Clock, Sparkles } from "lucide-react";

export default function SuccessStep({ customerInfo, total, bundleDiscount, isMaybeLater = false }) {
  // After the quote, send the visitor to the main Rolling Suds site (do NOT restart the form). The
  // form is embedded in an iframe on rollingsudsdupage.com, so navigate the PARENT window via
  // window.top (window.top === window when standalone, so this works in both). Per Edan (2026-07-07).
  const goHome = () => {
    window.scrollTo(0, 0);
    window.top.location.href = "https://rollingsudsdupage.com";
  };

  // Different content for "Maybe Later" vs accepted quote
  if (isMaybeLater) {
    return (
      <div className="max-w-lg mx-auto text-center animate-fade-in-up py-12" data-testid="step-5-success-maybe-later">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Clock className="w-10 h-10 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
          We'll Be Here When You're Ready!
        </h2>
        <p className="text-lg text-slate-700 mb-6">
          Thanks for your interest, {customerInfo.first_name}. Your information has been saved.
        </p>
        
        {/* What to expect */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 text-left">
          <h3 className="font-semibold text-blue-900 mb-4 text-center">What to Expect:</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-slate-700 text-sm">
                <strong>You're now subscribed to The Shiny Bubble!</strong> - Our weekly newsletter with cleaning tips, special offers, and exclusive discounts.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-slate-700 text-sm">
                <strong>We may reach out</strong> to see if you have any questions about our services or pricing.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-slate-700 text-sm">
                Ready to book? Call us at <a href="tel:6302306885" className="text-blue-600 font-semibold">(630) 230-6885</a>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-100 rounded-xl p-6 mb-6">
          <p className="text-sm text-slate-500 mb-2">Your estimated total was</p>
          <p className="text-4xl font-bold text-blue-600">${total}</p>
          {bundleDiscount > 0 && (
            <p className="text-sm text-emerald-600 mt-2">
              With ${bundleDiscount} bundle savings available!
            </p>
          )}
        </div>
        
        {/* Reminder about $50 credit */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Gift className="w-6 h-6 text-amber-600" />
            <p className="text-lg font-bold text-amber-800">Don't Forget!</p>
          </div>
          <p className="text-sm text-amber-700">
            When you're ready to accept your quote, you'll receive a <strong>$50 credit</strong> toward your next service!
          </p>
        </div>
        
        <Button
          onClick={goHome}
          className="btn-primary"
          data-testid="back-home-btn"
        >
          Back to Rolling Suds
        </Button>
      </div>
    );
  }

  // Standard success content for accepted quotes
  return (
    <div className="max-w-lg mx-auto text-center animate-fade-in-up py-12" data-testid="step-5-success">
      <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Check className="w-10 h-10 text-emerald-600" />
      </div>
      <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
        Thank You for Your Business!
      </h2>
      <p className="text-lg text-slate-700 mb-6">
        We appreciate you choosing Rolling Suds, {customerInfo.first_name}!
      </p>
      
      {/* What happens next */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 text-left">
        <h3 className="font-semibold text-blue-900 mb-4 text-center">What Happens Next:</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-slate-700 text-sm">
              <strong>Your estimate has been sent to {customerInfo.email}</strong> - Check your inbox (and spam folder) for the PDF estimate.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-slate-700 text-sm">
              <strong>We will contact you within 24 hours</strong> to confirm your preferred service date and time.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-slate-700 text-sm">
              Questions? Call us at <a href="tel:6302306885" className="text-blue-600 font-semibold">(630) 230-6885</a>
            </p>
          </div>
        </div>
      </div>

      <div className="bg-slate-100 rounded-xl p-6 mb-6">
        <p className="text-sm text-slate-500 mb-2">Your estimated total</p>
        <p className="text-4xl font-bold text-blue-600">${total}</p>
        {bundleDiscount > 0 && (
          <p className="text-sm text-emerald-600 mt-2">
            Including ${bundleDiscount} bundle savings!
          </p>
        )}
      </div>
      
      {/* $50 Credit Confirmation */}
      <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Gift className="w-6 h-6 text-emerald-600" />
          <p className="text-lg font-bold text-emerald-800">$50 Credit Issued!</p>
        </div>
        <p className="text-sm text-emerald-700">
          Your $50 credit has been saved and will be automatically applied to your next service at this address. Valid for 12 months.
        </p>
      </div>
      
      <Button
        onClick={goHome}
        className="btn-primary"
        data-testid="back-home-btn"
      >
        Back to Rolling Suds
      </Button>
    </div>
  );
}
